import { defaultTo, evolve, map, pipe, trim } from "ramda";
import { useCallback, useState } from "react";
import { notBlank } from "../../../components/Forms/validations";
import { useMsg } from "../../../components/Msg/Msg";
import { sessionsMessages } from "../messages";
import { ActionStepsStep } from "./ActionStepsStep";
import { AlignStep } from "./AlignStep";
import { AreaStep } from "./AreaStep";
import { GoalStep } from "./GoalStep";
import { MotivationStep } from "./MotivationStep";
import { ReflectStep } from "./ReflectStep";
import { SetActionStepsStep } from "./SetActionStepsStep";
import { SESSION_FIELDS } from "./constants";
import { useMotivationHints } from "./hints";

export const STEP_NAME = {
  AREA: "AREA",
  GOAL: "GOAL",
  MOTIVATION: "MOTIVATION",
  FIRST_ACTIONS: "FIRST_ACTIONS",
  ALIGN: "ALIGN",
  REFLECT: "REFLECT",
  REVIEW_ACTIONS: "REVIEW_ACTIONS",
};

export const useSteps = ({ steps, initialIndex = 0 }) => {
  const [activeStepIndex, setActiveStepIndex] = useState(initialIndex);
  const activeStep = steps[activeStepIndex];
  if (!activeStep) {
    debugger;
  }

  const nextStep = useCallback(() => {
    setActiveStepIndex((i) => i + 1);
  }, []);
  const prevStep = useCallback(() => {
    setActiveStepIndex((i) => Math.max(0, i - 1));
  }, []);

  return {
    steps,
    activeStep,
    activeStepIndex: Math.min(activeStepIndex, steps.length - 1),
    isFirst: activeStepIndex === 0,
    isLast: activeStepIndex === steps.length - 1,
    nextStep,
    prevStep,
  };
};

const FIELD_DEFS = {
  [SESSION_FIELDS.AREA_OF_DEVELOPMENT]: {
    map: pipe(defaultTo(""), trim, Array.of),
  },
  [SESSION_FIELDS.LONG_TERM_GOAL]: {
    map: pipe(defaultTo(""), trim),
    validate: { notBlank: notBlank() },
  },
  [SESSION_FIELDS.MOTIVATION]: {
    map: pipe(defaultTo(""), trim),
    validate: { notBlank: notBlank() },
  },
  [SESSION_FIELDS.ACTION_STEPS]: { map: map(evolve({ label: trim })) },
  [SESSION_FIELDS.REFLECTION]: {
    map: trim,
    validate: { notBlank: notBlank() },
  },
  [SESSION_FIELDS.PREV_ACTION_STEPS]: {},
};

export const useSessionStepsDefinition = () => {
  const msg = useMsg({ dict: sessionsMessages });
  const motivationHints = useMotivationHints();

  return {
    [STEP_NAME.AREA]: {
      stepName: STEP_NAME.AREA,
      StepComponent: AreaStep, // SessionStepCard
      fieldDefMap: {
        [SESSION_FIELDS.AREA_OF_DEVELOPMENT]:
          FIELD_DEFS[SESSION_FIELDS.AREA_OF_DEVELOPMENT],
      },
      label: msg("sessions.new.steps.area.label"),
      caption: msg("sessions.new.steps.area.caption"),
      iconName: "InsertChart",
      heading: msg("sessions.new.steps.area.heading"),
      perex: msg("sessions.new.steps.area.perex"),
    },
    [STEP_NAME.GOAL]: {
      stepName: STEP_NAME.GOAL,
      StepComponent: GoalStep, // FormStepCard - function as a child
      fieldDefMap: {
        [SESSION_FIELDS.LONG_TERM_GOAL]:
          FIELD_DEFS[SESSION_FIELDS.LONG_TERM_GOAL],
      },
      label: msg("sessions.new.steps.goal.label"),
      caption: msg("sessions.new.steps.goal.caption"),
      iconName: "Adjust",
      heading: msg("sessions.new.steps.goal.heading"),
      perex: msg("sessions.new.steps.goal.perex"),
    },
    [STEP_NAME.MOTIVATION]: {
      stepName: STEP_NAME.MOTIVATION,
      StepComponent: MotivationStep, // FormStepCard
      fieldDefMap: {
        [SESSION_FIELDS.MOTIVATION]: FIELD_DEFS[SESSION_FIELDS.MOTIVATION],
      },
      label: msg("sessions.new.steps.motivation.label"),
      caption: msg("sessions.new.steps.motivation.caption"),
      iconName: "RocketLaunch",
      heading: msg("sessions.new.steps.motivation.heading"),
      perex: msg("sessions.new.steps.motivation.perex"),
      focusedList: motivationHints,
    },
    [STEP_NAME.FIRST_ACTIONS]: {
      stepName: STEP_NAME.FIRST_ACTIONS,
      StepComponent: ActionStepsStep, // FormStepCard
      fieldDefMap: {
        [SESSION_FIELDS.ACTION_STEPS]: FIELD_DEFS[SESSION_FIELDS.ACTION_STEPS],
      },
      label: msg("sessions.new.steps.actionsteps.label"),
      caption: msg("sessions.new.steps.actionsteps.caption"),
      iconName: "Explore",
      heading: msg("sessions.new.steps.actionsteps.heading"),
      perex: msg("sessions.new.steps.actionsteps.perex"),
    },
    [STEP_NAME.ALIGN]: {
      stepName: STEP_NAME.ALIGN,
      StepComponent: AlignStep, // SessionStepCard
      label: msg("sessions.edit.steps.align.label"),
      caption: msg("sessions.edit.steps.align.caption"),
      iconName: "InsertChart",
      heading: msg("sessions.edit.steps.align.heading"),
      perex: msg("sessions.edit.steps.align.perex"),
    },
    [STEP_NAME.REFLECT]: {
      stepName: STEP_NAME.REFLECT,
      StepComponent: ReflectStep, // FormStepCard
      fieldDefMap: {
        [SESSION_FIELDS.REFLECTION]: FIELD_DEFS[SESSION_FIELDS.REFLECTION],
        [SESSION_FIELDS.PREV_ACTION_STEPS]:
          FIELD_DEFS[SESSION_FIELDS.PREV_ACTION_STEPS],
      },
      label: msg("sessions.edit.steps.reflect.label"),
      caption: msg("sessions.edit.steps.reflect.caption"),
      iconName: "Lightbulb",
      heading: msg("sessions.edit.steps.reflect.heading"),
      perex: msg("sessions.edit.steps.reflect.perex"),
    },
    [STEP_NAME.REVIEW_ACTIONS]: {
      stepName: STEP_NAME.REVIEW_ACTIONS,
      StepComponent: SetActionStepsStep, // FormStepCard
      fieldDefMap: {
        [SESSION_FIELDS.ACTION_STEPS]: FIELD_DEFS[SESSION_FIELDS.ACTION_STEPS],
        [SESSION_FIELDS.PREV_ACTION_STEPS]:
          FIELD_DEFS[SESSION_FIELDS.PREV_ACTION_STEPS],
      },
      label: msg("sessions.edit.steps.setaction.label"),
      caption: msg("sessions.edit.steps.setaction.caption"),
      iconName: "Explore",
      heading: msg("sessions.edit.steps.setaction.heading"),
      perex: msg("sessions.edit.steps.setaction.perex"),
    },
  };
};
